import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { getUser, getPagePermissions } from '../state/authSlice';
import { getCourierSetting, getCourier, getIsPartnerLinked } from '../state/courierSlice';
import ProtectedRoute from '@shared/components/ProtectedRoute';
import { AuthRoutePath } from '../config/routePaths';
import { userCanAccessPage, hasExplicitPagePermission } from '../config/pagePermissions';
import { canOverlordBypassBillingLock, canFinishOnboardingDuringBillingLock } from '../utils/billingBypass';
import { hasAnyWarehouseApiEnabled } from '../utils/warehouse';
import { hasAnyVendorApiKey } from '../utils/courierSecrets';

export interface AdminProtectedRouteProps {
  element: React.ReactElement;
  allowedRoles?: string[];
  requiresApiKey?: boolean;
  requiresFeatureFlag?: string;
}

const AdminProtectedRoute: React.FC<AdminProtectedRouteProps> = ({
  element,
  allowedRoles = [],
  requiresApiKey = false,
  requiresFeatureFlag,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(getUser);
  const courier = useSelector(getCourier);
  const courierSettings = useSelector(getCourierSetting);
  const pagePermissions = useSelector(getPagePermissions);
  // Resolved with the courier in fetchCourier, so it is already settled by the
  // time routes render (App.tsx blocks on isCourierLoading).
  const isPartnerLinked = useSelector(getIsPartnerLinked);

  // Check if any API key exists
  const hasApiKey = !!(hasAnyVendorApiKey(courierSettings) ||
                      courierSettings?.isLogisApiEnabled ||
                      courierSettings?.isHnmApiEnabled ||
                      courierSettings?.isCargoJaApiEnabled ||
                      courierSettings?.isOnePlusApiEnabled ||
                      courierSettings?.isTrGlobalApiEnabled) ||
                      hasAnyWarehouseApiEnabled(courierSettings);

  // A courier whose warehouse is another OX tenant (reached through a
  // PartnerLink, e.g. Kajay) never gets a vendor API key, but Warehouse
  // Management has real content for it — see Courier.fetchWarehousePackages.
  const satisfiesApiKeyGate = hasApiKey || isPartnerLinked;

  // Check if user has access to the current page based on custom permissions or role
  // defaults. Custom permissions take precedence; see userCanAccessPage for the shared
  // logic (also used by AdminShell's subscription-lock redirect).
  // Get the page key from current path (remove leading slash)
  const pageKey = location.pathname.replace(/^\//, '');

  const hasPageAccess = React.useMemo(
    () => userCanAccessPage(user, pagePermissions, pageKey),
    [user, pagePermissions, pageKey],
  );

  // An explicit custom permission is authoritative. When one exists, the route's
  // hard-coded allowedRoles must not run first, otherwise a user granted a page
  // outside their role defaults (e.g. a Cashier given Communication) is bounced to
  // /unauthorized before the grant is ever consulted. A custom deny is still
  // enforced by the hasPageAccess redirect below.
  const hasExplicitPermission = hasExplicitPagePermission(pagePermissions, pageKey);

  // Redirect to billing page if courier is inactive and not already on billing page.
  // Overlords can still reach Settings/Users/Couriers so they can fix the account.
  React.useEffect(() => {
    if (!courier || courier.isActive || location.pathname === '/billing') return;
    if (canOverlordBypassBillingLock(user, location.pathname)) return;
    if (canFinishOnboardingDuringBillingLock(courier, location.pathname)) return;
    navigate('/billing');
  }, [courier, user, location.pathname, navigate]);

  // Redirect to unauthorized if user doesn't have page permission
  React.useEffect(() => {
    if (user && !hasPageAccess) {
      navigate('/unauthorized');
    }
  }, [user, hasPageAccess, navigate]);

  // Redirect if feature flag is not enabled
  React.useEffect(() => {
    if (requiresFeatureFlag && courierSettings && !(courierSettings as any)[requiresFeatureFlag]) {
      navigate('/dashboard', { replace: true });
    }
  }, [requiresFeatureFlag, courierSettings, navigate]);

  const protectedRouteUser = user ? {
    roles: user.roles
  } : null;

  return (
    <ProtectedRoute
      element={element}
      allowedRoles={hasExplicitPermission ? [] : allowedRoles}
      requiresApiKey={requiresApiKey}
      hasApiKey={satisfiesApiKeyGate}
      unauthorizedPath="/unauthorized"
      loginPath={AuthRoutePath}
      user={protectedRouteUser}
    />
  );
};

export default AdminProtectedRoute;
